import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    AppBar, Toolbar, IconButton, Typography, Box, Badge,
    Menu, MenuItem, useMediaQuery, useTheme
} from "@mui/material";
import { People, Settings } from "@mui/icons-material";
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import MenuIcon from '@mui/icons-material/Menu'; // Import the Menu icon

// Assume these imports are correct
import { genreChannelsList } from "../liveshows/channelGenre";
import {checkSuperUser, userLogOut, valideLogin} from "../login/UserService";

// Assuming 'header.css' defines .typo-genre and .type-genre-selected
// For better responsiveness, these styles are partially moved to the sx prop.

const Header = () => {
    const theme = useTheme();
    // Check if the screen is wider than the 'md' breakpoint (Desktop/Large Tablet)
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const [superUser, setSuperUser] = useState({});
    const [anchorEl, setAnchorEl] = useState(null); // State for the menu
    const menuOpen = Boolean(anchorEl);

    const params = useParams();
    const channelGenreId = params.id;

    useEffect(() => {
        async function getSuperInfos() {
            const getSuperUser = await checkSuperUser();
            if (getSuperUser) {
                setSuperUser(getSuperUser);
            }

            const valide = await valideLogin()
            if (!valide) {
                window.location.href ="/"
            }

        }
        getSuperInfos();
    }, []);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const navigateToGenre = (id) => {
        handleMenuClose();
        window.location.href = "/genre/" + id;
    };

    // Define common styles for the genre links
    const navItemStyles = {
        fontFamily: "Orbitron",
        cursor: 'pointer',
        padding: isDesktop ? '0 16px' : '0 8px', // Adjust padding based on screen size
        transition: 'color 0.2s',
        whiteSpace: 'nowrap', // Prevents wrapping for genre names
        display: 'block', // Ensure Typography behaves like a block element
        '&:hover': {
            opacity: 0.8,
        },
    };

    // Icon size adjustment
    const iconSize = isDesktop ? 40 : 28;

    return (
        <Box sx={{ flexGrow: 1, marginBottom: 5 }}>
            <AppBar sticky={"top"} color="primary">
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    {/* 1. Live Button (Left Side) */}
                    <IconButton
                        className={channelGenreId ? 'typo-genre' : 'type-genre-selected'}
                        onClick={() => { window.location.href = "/live" }}
                        size="large"
                        color="inherit"
                        sx={{
                            // Adjust margin for mobile
                            marginRight: isDesktop ? 2 : 0,
                            padding: isDesktop ? '6px 12px' : '4px',
                        }}
                    >
                        {isDesktop && (
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontFamily: "Orbitron", marginRight: 1 }}>
                                Live
                            </Typography>
                        )}
                        <LiveTvIcon sx={{ fontSize: iconSize, color: "white", marginLeft: isDesktop ? 1 : 0 }} />
                    </IconButton>

                    {/* 2. Genre Navigation (Center) */}
                    {isDesktop ? (
                        // Desktop View: Show genre names inline
                        <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'flex-start', overflowX: 'auto' }}>
                            {genreChannelsList().map((genreChannel) => (
                                <Typography
                                    className={parseInt(channelGenreId) === genreChannel?.id ? 'type-genre-selected' : 'typo-genre'}
                                    onClick={() => { window.location.href = "/genre/" + genreChannel?.id }}
                                    key={genreChannel.id}
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        ...navItemStyles,
                                        // Use MUI's theme for selected color if 'type-genre-selected' is not defined
                                        color: parseInt(channelGenreId) === genreChannel?.id ? theme.palette.secondary.main : 'inherit',
                                    }}
                                >
                                    {genreChannel.name}
                                </Typography>
                            ))}
                        </Box>
                    ) : (
                        // Mobile View: Show Hamburger Menu
                        <Box sx={{ flexGrow: 1 }} /> // Spacer to push menu icon to the right
                    )}


                    {/* 3. Action Icons (Right Side) */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* Hamburger Menu (Mobile Only) */}
                        {!isDesktop && (
                            <>
                                <IconButton
                                    size="large"
                                    aria-label="menu of genres"
                                    aria-controls={menuOpen ? 'genre-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={menuOpen ? 'true' : undefined}
                                    onClick={handleMenuOpen}
                                    color="inherit"
                                >
                                    <MenuIcon sx={{ fontSize: iconSize }} />
                                </IconButton>
                                <Menu
                                    id="genre-menu"
                                    anchorEl={anchorEl}
                                    open={menuOpen}
                                    onClose={handleMenuClose}
                                >
                                    {genreChannelsList().map((genreChannel) => (
                                        <MenuItem
                                            style={{fontFamily:"Orbitron"}}
                                            key={genreChannel.id}
                                            onClick={() => navigateToGenre(genreChannel.id)}
                                            selected={parseInt(channelGenreId) === genreChannel?.id}
                                        >
                                            {genreChannel.name}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}

                        {/* Notifications Icon */}
                        <IconButton size="large" color="inherit">
                            <Badge badgeContent={17} color="error">
                                <NotificationsIcon sx={{ fontSize: iconSize, marginLeft: isDesktop ? 3 : 1 }} />
                            </Badge>
                        </IconButton>

                        {/* Settings Icon */}
                        {superUser?.totalPeople > 0 && (
                            <IconButton size="large" color="inherit">
                                <Settings onClick={()=>{
                                    window.location.href = "/system-infos"
                                }} sx={{ fontSize: iconSize, marginLeft: isDesktop ? 3 : 1 }} />
                            </IconButton>
                        )}

                        {/* SuperUser People Icon (Conditional) */}
                        {superUser?.totalPeople > 0 && (
                            <IconButton size="large" color="inherit">
                                <Badge badgeContent={superUser?.totalPeople} color="error">
                                    <People sx={{ fontSize: iconSize, marginLeft: isDesktop ? 3 : 1 }} />
                                </Badge>
                            </IconButton>
                        )}

                        {/* Logout Icon */}
                        <IconButton onClick={userLogOut} size="large" aria-label="log out user" color="inherit">
                            <LogoutIcon sx={{ fontSize: iconSize, marginLeft: isDesktop ? 3 : 1 }} />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>
        </Box>
    );
}
export default  Header